import * as React from 'react'
import { useParams } from 'react-router-dom'
import SearchInput from '@/modules/drivers/shared/SearchInput'
import SearchColor from '@/assets/icons_new/SearchColor.svg'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { FaIcon, faPrintSolid, faFileInvoice, faArrowsRotate } from '@/lib/fa'
import { toast } from '@/lib/toasts/api'
import { refreshDriverViolations } from '@/lib/api/company/violation/refresh/refreshDriverViolations'
import { useInvalidateDriverRequiredActions } from '@/lib/api/company/drivers/useQueryDriverRequiredActions'
import { useScopeBusy } from '@/modules/shared/hooks/useScopeBusy'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import usePrintViolations from '../hooks/usePrintViolations'
import { getExcludedViolations } from '@/modules/drivers/stores/violationPrintStore'
import ViolationsPrintModal, {
  ViolationPrintReport,
} from './ViolationsPrintModal'

interface ViolationsSingleActionBarProps {
  search: string
  setSearch: (search: string) => void
  searchResults: number
  driverId: number | string
  onRefreshed: () => void
}

const ViolationsSingleActionBar: React.FC<ViolationsSingleActionBarProps> = ({
  search,
  setSearch,
  searchResults,
  driverId,
  onRefreshed,
}) => {
  const { t } = useLocalize()
  const { companyId } = useParams<{ companyId: string }>()
  const [showSearch, setShowSearch] = React.useState(false)
  const [printModalOpen, setPrintModalOpen] = React.useState(false)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const invalidateRequiredActions = useInvalidateDriverRequiredActions()
  const violationsBusy = useScopeBusy(
    companyId ? taskScopeKey.violations(companyId) : null,
  )
  const refreshBlockedText = violationsBusy.busy
    ? violationsBusy.startedBy
      ? `${violationsBusy.startedBy}: ${t('driverViolations.calculation-in-progress')}`
      : t('driverViolations.calculation-in-progress')
    : undefined
  const searchContainerRef = React.useRef<HTMLDivElement>(null)

  const { isGenerating, busyTooltip, printReports, handlePrintInstruction } =
    usePrintViolations()

  const printArgs = () => ({
    selectedIds: [driverId],
    drivers: [{ id: driverId }],
    companyId,
    excludedViolations: getExcludedViolations(companyId),
  })

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        if (setSearch) setSearch('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [setSearch])

  const handleRefresh = async () => {
    if (!companyId || isRefreshing || violationsBusy.busy) return
    setIsRefreshing(true)
    try {
      await refreshDriverViolations({ companyId, driverId: Number(driverId) })
      invalidateRequiredActions()
      onRefreshed()
    } catch {
      toast.error({ text: t('driverViolations.calculated-error') })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleGenerate = async (selectedReports: ViolationPrintReport[]) => {
    await printReports(selectedReports, printArgs())
    setPrintModalOpen(false)
  }

  return (
    <div className="tw-relative tw-z-[100] tw-flex tw-w-full tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-relative" ref={searchContainerRef}>
          <div
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-px-1 ${showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'}`}
            onClick={() => setShowSearch((prev) => !prev)}
          >
            <img src={SearchColor} alt="Search Icon" className="tw-px-1" />
            <p className="tw-text-sm tw-font-medium tw-text-primary">
              {t('common.search')}
            </p>
          </div>
          {showSearch && setSearch && (
            <SearchInput
              search={search}
              searchResults={searchResults}
              setSearch={setSearch}
              placeholder={t('driverModule.search-violations') || ''}
              id="search-violations-single-input"
            />
          )}
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-gap-3">
        <Tooltip
          text={t('driverModule.refresh-violations')}
          onClick={handleRefresh}
          disabled={isRefreshing}
          blockedText={refreshBlockedText}
        >
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faArrowsRotate}
              className={`tw-h-4 tw-w-4 tw-text-[#2448bb] ${isRefreshing ? 'tw-animate-spin' : ''}`}
            />
          </div>
        </Tooltip>
        <Tooltip
          text={t('menu.print')}
          onClick={() => setPrintModalOpen(true)}
          blockedText={busyTooltip}
        >
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faPrintSolid}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>
        <Tooltip
          text={t('driverModule.print-instruction')}
          onClick={() => handlePrintInstruction(printArgs())}
          blockedText={busyTooltip}
        >
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faFileInvoice}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>
      </div>

      <ViolationsPrintModal
        isOpen={printModalOpen}
        pending={isGenerating}
        onClose={() => setPrintModalOpen(false)}
        onGenerate={handleGenerate}
      />
    </div>
  )
}

export default ViolationsSingleActionBar
